import { Plus, Users2, UserCheck, UserX, Mail, Calendar, Trash2 } from 'lucide-react';

/**
 * La gestion des comptes poseurs par leur installateur.
 *
 * Trois gestes possibles sur une ligne : modifier, désactiver/réactiver, supprimer.
 * Les deux derniers passent par une confirmation portée par le parent — ce sont les
 * seuls endroits du parcours où un installateur peut couper l'accès d'un collègue qui
 * est peut-être en train de faire signer un client.
 */
export default function MonEquipe({ poseurs, t, onCreer, onModifier, onBasculer, onSupprimer }) {
  const chantierMot = (n) => (n > 1 ? t('chantier_plural') : t('chantier_singular'));

  return (
    <div className="space-y-4" data-testid="installateur-equipe">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-neutral-900">{t('team_title')}</h1>
          <p className="text-[12px] text-neutral-500">{t('team_subtitle')}</p>
        </div>
        <button
          onClick={onCreer}
          className="px-3 py-2 bg-marque text-white rounded-lg text-[12px] font-medium flex items-center gap-1.5 hover:bg-marque-clair active:scale-[0.98]"
          data-testid="equipe-add-btn"
        >
          <Plus className="w-4 h-4" /> {t('team_new_poseur')}
        </button>
      </div>

      {poseurs.length === 0 ? (
        <div className="bg-white border border-neutral-200 rounded-xl p-8 text-center">
          <Users2 className="w-10 h-10 text-neutral-300 mx-auto mb-3" />
          <p className="text-[13px] text-neutral-600 font-medium">{t('team_no_poseur_title')}</p>
          <p className="text-[11.5px] text-neutral-400 mt-1">{t('team_no_poseur_desc')}</p>
        </div>
      ) : (
        <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
          <ul className="divide-y divide-neutral-100">
            {poseurs.map((p) => (
              <li key={p.id} className="p-3 flex items-center gap-3" data-testid={`equipe-row-${p.id}`}>
                <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                  p.active ? 'bg-emerald-100 text-emerald-700' : 'bg-neutral-100 text-neutral-400'
                }`}>
                  {p.active ? <UserCheck className="w-4 h-4" /> : <UserX className="w-4 h-4" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] font-medium text-neutral-900 truncate">{p.name}</p>
                  <p className="text-[10.5px] text-neutral-500 truncate flex items-center gap-1">
                    <Mail className="w-3 h-3" /> {p.email}
                  </p>
                  <p className="text-[10px] text-neutral-400 flex items-center gap-1 mt-0.5">
                    <Calendar className="w-3 h-3" />
                    {t('team_created_on')}{' '}
                    {p.created_at ? new Date(p.created_at).toLocaleDateString('fr-FR') : '—'} ·{' '}
                    {p.nb_chantiers} {chantierMot(p.nb_chantiers)}
                  </p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => onModifier(p)}
                    className="text-[11px] text-neutral-600 hover:text-neutral-900 px-2 py-1 rounded hover:bg-neutral-100"
                    data-testid={`equipe-edit-${p.id}`}
                  >
                    {t('team_edit')}
                  </button>
                  <button
                    onClick={() => onBasculer(p)}
                    className={`text-[11px] font-medium px-2 py-1 rounded ${
                      p.active ? 'text-red-600 hover:bg-red-50' : 'text-emerald-600 hover:bg-emerald-50'
                    }`}
                    data-testid={`equipe-toggle-${p.id}`}
                  >
                    {p.active ? t('team_deactivate') : t('team_reactivate')}
                  </button>
                  <button
                    onClick={() => onSupprimer(p)}
                    className="text-[11px] font-medium px-2 py-1 rounded text-neutral-400 hover:text-red-700 hover:bg-red-50"
                    data-testid={`equipe-delete-${p.id}`}
                    title={t('team_delete')}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
